import { SVGMap } from "react-svg-map";
import nigeria from "@svg-maps/nigeria";
import usa from "@svg-maps/usa";
import mexico from "@svg-maps/mexico";
import spain from "@svg-maps/spain";
import brazil from "@svg-maps/brazil";
import germany from "@svg-maps/germany";
import "react-svg-map/lib/index.css";
import { scaleLinear } from "d3-scale";
import { useState, useRef, useMemo } from "react";
import "./stylesGeographicUserInsights.css";

// Red scale endpoints — light red near 0, deep red for the most negative value.
const NEGATIVE_RANGE = ["#FADBD8", "#C0392B"]; // [lightest, deepest]

// Legend strip, dark -> light, mirroring the blue blocks below.
const NEGATIVE_LEGEND_BLOCKS = [
  "#C0392B",
  "#CB4335",
  "#D6584B",
  "#E07567",
  "#EA9387",
  "#F1B0A8",
  "#F8D0CB",
  "#FADBD8",
];

export default function GeographicUserInsights({
  mapId = "default",
  country = "USA",
  data = [],
  isDollars = false,
  title = null,
  /** When true, states with amount < 0 render in red (e.g. Sportsbook P&L). */
  highlightNegativeValues = false,
  /** Symbol used when `isDollars` is true. Default "$" preserves legacy output. */
  currencySymbol = "$",
}) {
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState({ show: false, text: "", x: 0, y: 0 });

  // Select the appropriate map based on country
  const getMap = () => {
    switch (country.toUpperCase()) {
      case "NIGERIA":
      case "NGA":
        return nigeria;
      case "MEXICO":
      case "MEX":
        return mexico;
      case "SPAIN":
        return spain;
      case "BRAZIL":
      case "BR":
      case "BRA":
        return brazil;
      case "GERMANY":
      case "DE":
      case "DEU":
        return germany;
      case "USA":
      default:
        return usa;
    }
  };

  const map = getMap();

  const amountObj = data.map((s) => ({
    name: s.name?.toLowerCase(),
    amount: s.amount,
  }));

  const positiveAmounts = data
    .map((d) => d.amount)
    .filter((amount) => amount > 0);
  const maxPositive = positiveAmounts.length
    ? Math.max(...positiveAmounts)
    : 0;

  const colorScale = scaleLinear()
    .domain([0, maxPositive])
    .range(["#dfedfe", "#28388a"]);

  const negativeAmounts = data
    .map((d) => d.amount)
    .filter((amount) => amount < 0);
  // Most negative value (a negative number); anchors the deepest red.
  const minNegative = negativeAmounts.length ? Math.min(...negativeAmounts) : 0;

  // domain [0, minNegative]: amount 0 -> lightest red, most-negative -> deepest red.
  const negativeColorScale = scaleLinear()
    .domain([0, minNegative])
    .range(NEGATIVE_RANGE);

  const hasNegativeValues =
    highlightNegativeValues && data.some((d) => d.amount < 0);

  const getColor = (name, darker = false) => {
    const state = data.find(
      (d) => d.name?.toLowerCase() === name?.toLowerCase()
    );

    if (!state || state.amount === 0) return "#cfd7e6";

    if (highlightNegativeValues && state.amount < 0) {
      // amount < 0 guarantees a non-zero negative domain, so the scale is safe here.
      return darker
        ? negativeColorScale(state.amount * 1.5)
        : negativeColorScale(state.amount);
    }

    if (state.amount < 0) return "#cfd7e6";

    return darker ? colorScale(state.amount * 1.5) : colorScale(state.amount);
  };

  // Prefix the BE-configured symbol manually — Intl only knows ISO codes.
  // Sign sits outside the symbol: "-$614.95", not "$-614.95".
  const formatLegendValue = (value) => {
    const formatted = Intl.NumberFormat("en-US", {
      minimumFractionDigits: isDollars ? 2 : 0,
      maximumFractionDigits: 2,
    }).format(Math.abs(value));
    const sign = value < 0 ? "-" : "";
    return isDollars ? `${sign}${currencySymbol}${formatted}` : `${sign}${formatted}`;
  };

  const getTheBiggestValue = (data, positivesOnly = false) => {
    if (data.length === 0) return 0;

    const validData = data.filter((d) => d.name != null);

    if (validData.length === 0) return 0;

    const amounts = positivesOnly
      ? validData.map((d) => d.amount).filter((amount) => amount > 0)
      : validData.map((d) => d.amount);

    if (amounts.length === 0) return 0;

    const max = Math.max(...amounts);

    if (isNaN(max)) return 0;
    return formatLegendValue(max);
  };

  // Mirror of getTheBiggestValue for the red (negative) legend label.
  const getTheSmallestValue = (data) => {
    const amounts = data
      .filter((d) => d.name != null)
      .map((d) => d.amount)
      .filter((amount) => amount < 0);

    if (amounts.length === 0) return 0;

    const min = Math.min(...amounts);

    if (isNaN(min)) return 0;
    return formatLegendValue(min);
  };

  const dynamicStyles = useMemo(
    () =>
      map.locations
        .map((state) => {
          const cls = `map-${mapId
            .replace(/\s+/g, "-")
            .toLowerCase()}-state-${state.name
            .replace(/\s+/g, "-")
            .toLowerCase()}`;
          const color = getColor(state.name);
          const hoverColor = getColor(state.name, true);
          return `
          #map-container-${mapId} .${cls},
          #map-container-${mapId} .svg-map .${cls},
          .${cls}.svg-map__location,
          .${cls} {
            fill: ${color} !important;
            stroke: #333 !important;
            transition: 0.2s;
          }
          #map-container-${mapId} .${cls}:hover,
          .${cls}:hover {
            fill: ${hoverColor} !important;
            transform: scale(1.01);
          }
        `;
        })
        .join(""),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, country, title, map.locations, mapId, highlightNegativeValues]
  );

  return (
    <div
      className="geographic-user-insights-container"
      id={`map-container-${mapId}`}
      style={{ position: "relative" }}
      ref={containerRef}
    >
      <div className="geographic-user-insights-header">
        {title && <h3>{title}</h3>}
      </div>
      <SVGMap
        map={map}
        id={`map-${mapId}`}
        onLocationMouseOver={(locationId) => {
          const loc = map.locations.find((l) => l.id === locationId.target.id);

          const stateName = loc.name.toLowerCase();
          const amount =
            amountObj.find((s) => s.name === stateName)?.amount ??
            "No Data Available";
          setTooltip({
            show: true,
            text: {
              name: loc.name,
              amount: amount,
            },
          });
        }}
        onLocationMouseOut={() => setTooltip((t) => ({ ...t, show: false }))}
        onLocationMouseMove={(e) => {
          if (!containerRef.current) return;

          const rect = containerRef.current.getBoundingClientRect();
          const x = e.clientX - rect.left + 12;
          const y = e.clientY - rect.top + 12;

          const safeX = Math.min(x, rect.width - 160);
          const safeY = Math.min(y, rect.height - 40);

          setTooltip((t) => ({
            ...t,
            x: safeX,
            y: safeY,
          }));
        }}
        locationClassName={(loc) =>
          `map-${mapId.replace(/\s+/g, "-").toLowerCase()}-state-${loc.name
            .replace(/\s+/g, "-")
            .toLowerCase()}`
        }
      />

      <div className="geographic-user-insights-info">
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span>
            {getTheBiggestValue(data, hasNegativeValues)}
          </span>
          <div className="geographic-user-insights-info-blocks">
            <div
              className="geographic-user-insights-info-block"
              style={{ backgroundColor: "#28388A" }}
            ></div>
            <div
              className="geographic-user-insights-info-block"
              style={{ backgroundColor: "#3C4EA9" }}
            ></div>
            <div
              className="geographic-user-insights-info-block"
              style={{ backgroundColor: "#4F62C9" }}
            ></div>
            <div
              className="geographic-user-insights-info-block"
              style={{ backgroundColor: "#6177E8" }}
            ></div>
            <div
              className="geographic-user-insights-info-block"
              style={{ backgroundColor: "#7A9BEF" }}
            ></div>
            <div
              className="geographic-user-insights-info-block"
              style={{ backgroundColor: "#92BFF5" }}
            ></div>
            <div
              className="geographic-user-insights-info-block"
              style={{ backgroundColor: "#C2DAF5" }}
            ></div>
            <div
              className="geographic-user-insights-info-block"
              style={{ backgroundColor: "#CFD7E6" }}
            ></div>
          </div>
          <span>0</span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div className="geographic-user-insights-info-blocks">
            <div
              className="geographic-user-insights-info-block"
              style={{ backgroundColor: "#2F2F39" }}
            ></div>
          </div>
          <span>Unavailable</span>
        </div>

        {hasNegativeValues ? (
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span>{getTheSmallestValue(data)}</span>
            <div className="geographic-user-insights-info-blocks">
              {NEGATIVE_LEGEND_BLOCKS.map((color) => (
                <div
                  key={color}
                  className="geographic-user-insights-info-block"
                  style={{ backgroundColor: color }}
                ></div>
              ))}
            </div>
            <span>0</span>
          </div>
        ) : null}
      </div>
      {/* tooltip */}
      {tooltip.show && (
        <div
          className="geographic-user-insights-tooltip"
          data-testid="geographic-user-insights-tooltip"
          style={{
            position: "absolute",
            left: tooltip.x,
            top: tooltip.y,
            background: "#000",
            padding: "6px 10px",
            color: "#fff",
            borderRadius: 4,
            fontSize: 13,
            pointerEvents: "none",
            whiteSpace: "nowrap",
          }}
        >
          <span className="geographic-user-insights-tooltip__state">
            {tooltip.text?.name?.toUpperCase()}
          </span>
          <span className="geographic-user-insights-tooltip__metric">
            {typeof tooltip.text?.amount === "number"
              ? (() => {
                  // Sign sits outside the symbol: "-$100", not "$-100".
                  const amount = tooltip.text.amount;
                  const sign = amount < 0 ? "-" : "";
                  const abs = Intl.NumberFormat("en-US", {
                    style: "decimal",
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                  }).format(Math.abs(amount));
                  return `${sign}${isDollars ? currencySymbol : ""}${abs}`;
                })()
              : `${isDollars ? currencySymbol : ""}0`}
            {!isDollars && " Users"}
          </span>
        </div>
      )}

      <style>{dynamicStyles}</style>
    </div>
  );
}
